import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  api,
  APIError,
  isQuotaError,
  type ChatMessage,
  type ChatProposal,
  type ContentLang,
  type CourseFormat,
  type RewriteContext,
  type SuggestedAT,
  type SuggestedILO,
  type SuggestedTLA,
  type TLAPhase,
  type WizardCatalog,
  type WizardContextKind,
  type WizardCourseMeta,
} from "@/api/client";
import AiAssistButton from "@/components/AiAssistButton";
import CreditCost from "@/components/CreditCost";
import DidaktikChat from "@/components/DidaktikChat";
import Header from "@/components/Layout/Header";
import LinkedILOsEditor from "@/components/LinkedILOsEditor";
import LoadingOverlay from "@/components/Loading";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";
import StepIndicator from "@/components/StepIndicator";
import { DEPTH_LABELS, type DepthLabel } from "@/lib/depth";
import { usePhaseOptions } from "@/lib/labels";
import { remapSwappedIndices, swapItems } from "@/lib/reorder";

// 3 Bildungskontexte (Konsolidierung 2026-07-18, docs/format-konsolidierung.md).
// Backend bekommt den `context_kind` als Hint für suggest_ilos/_tlas/_ats;
// die Format-Liste pro Kontext liefert der Katalog (context_formats).
// Labels/Subs sind UI-seitig → i18n (contexts.*, wizard.contextSub.*).
const CONTEXT_KEYS: WizardContextKind[] = ["hochschule", "ausbildung", "corporate_ld"];

const CONTEXT_LABEL_KEYS: Record<WizardContextKind, { label: string; sub: string }> = {
  hochschule: { label: "contexts.hochschule", sub: "wizard.contextSub.hochschule" },
  ausbildung: { label: "contexts.ausbildung", sub: "wizard.contextSub.ausbildung" },
  corporate_ld: { label: "contexts.corporate_ld", sub: "wizard.contextSub.corporate_ld" },
};

// ──────────────────────────────────────────────────────────────────────
// Wizard-Flow für die geführte Course-Neuerstellung.
// 4 Steps: Input → ILOs → TLAs → ATs. Jede KI-Stufe liefert Vorschläge,
// die User editieren oder verwerfen kann. Am Ende wird ein Course angelegt
// und der Editor geöffnet (Audit erfolgt dort, wie beim Extract-Pfad).
// ──────────────────────────────────────────────────────────────────────

type WizardStep = 1 | 2 | 3 | 4;

export default function Wizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<WizardStep>(1);
  // Maximal besuchter Step — besuchte Steps sind per Step-Leiste frei
  // anspringbar (State bleibt im Component-State erhalten), vorwärts geht
  // nur bis maxStep + 1 und nur mit der Weiter-Guard des jeweiligen Steps.
  const [maxStep, setMaxStep] = useState<WizardStep>(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);

  // 402 (Guthaben aufgebraucht) bekommt die Quota-Karte statt Fehlertext.
  const handleSuggestError = (e: unknown, fallbackKey: string) => {
    if (isQuotaError(e)) setQuota(e.data);
    else setError(e instanceof APIError ? e.detail : t(fallbackKey));
  };

  const goStep = (n: WizardStep) => {
    setStep(n);
    setMaxStep((m) => (n > m ? n : m));
  };

  // Step 1
  const [contextKind, setContextKind] = useState<WizardContextKind | null>(null);
  const [title, setTitle] = useState("");
  const [targetGroup, setTargetGroup] = useState("");
  const [courseFormat, setCourseFormat] = useState<string>("");
  const [customFormat, setCustomFormat] = useState("");
  const [durationHours, setDurationHours] = useState<string>("");
  const [roughIdea, setRoughIdea] = useState("");
  // Sprache der Kursinhalte (unabhängig von der UI-Sprache): steuert die
  // Sprache der KI-Vorschläge und wird als metadata.content_lang persistiert.
  const [contentLang, setContentLang] = useState<ContentLang>("de");
  // Freitext-Strategie (C2/C3): geht als `strategy`-Body-Param an alle vier
  // Suggest-Calls und als Kontext an die kurs-lose ✨-Feld-Assistenz
  // (/assist/rewrite ohne course_id — der Wizard ist stateless).
  const [strategyHint, setStrategyHint] = useState("");

  // Kurs-loser Kontext für die ✨-Assistenz in den Steps 2–4.
  const assistContext: RewriteContext = {
    title,
    audience: targetGroup,
    rough_idea: roughIdea,
    strategy: strategyHint.trim() || undefined,
  };

  // Bei „Anderes Format" zählt der Freitext (Fallback: die ID selbst).
  const effectiveFormat =
    courseFormat === "anderes" ? customFormat.trim() || "anderes" : courseFormat;

  // Vorschläge der drei Stufen
  const [ilos, setIlos] = useState<SuggestedILO[]>([]);
  const [tlas, setTlas] = useState<SuggestedTLA[]>([]);
  const [ats, setAts] = useState<SuggestedAT[]>([]);

  // Didaktik-Chat (Block 2): History + hängender Vorschlag liegen hier im
  // Wizard-State, damit sie Step-Wechsel überstehen (DidaktikChat wird pro
  // Step neu gemountet).
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatProposal, setChatProposal] = useState<ChatProposal | null>(null);

  // „Übernehmen" ersetzt die komplette Liste der Ziel-Kategorie
  // (inkl. linked_ilo_indices) — der Vorschlag enthält immer die ganze Liste.
  function applyChatProposal(p: ChatProposal) {
    if (p.target === "ilos") setIlos(p.items as SuggestedILO[]);
    else if (p.target === "tlas") setTlas(p.items as SuggestedTLA[]);
    else setAts(p.items as SuggestedAT[]);
    setChatProposal(null);
  }

  // ILO an Position `i` um `dir` (-1 = hoch, +1 = runter) verschieben.
  // Die TLA/AT-Verlinkungen sind index-basiert (linked_ilo_indices zeigen
  // auf Positionen im ilos-Array) — beim Tausch müssen die beiden
  // betroffenen Indizes in ALLEN Links mitremappt werden, sonst zeigen
  // sie danach auf das falsche Lernziel.
  function moveIlo(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= ilos.length) return;
    setIlos(swapItems(ilos, i, j));
    setTlas(tlas.map((tla) => ({
      ...tla,
      linked_ilo_indices: remapSwappedIndices(tla.linked_ilo_indices ?? [], i, j),
    })));
    setAts(ats.map((at) => ({
      ...at,
      linked_ilo_indices: remapSwappedIndices(at.linked_ilo_indices ?? [], i, j),
    })));
  }

  // Catalog (Klartext-Labels für Dropdowns) — einmal pro Wizard-Mount laden
  const [catalog, setCatalog] = useState<WizardCatalog | null>(null);
  useEffect(() => {
    api.wizardCatalog().then(setCatalog).catch(() => {
      // Wenn der Katalog nicht lädt, fallen wir auf Free-Text-Edit zurück
      setCatalog(null);
    });
  }, []);

  const courseMeta: WizardCourseMeta = {
    title,
    target_group: targetGroup,
    course_format: effectiveFormat || null,
    duration_hours: durationHours ? Number(durationHours) : null,
    context_kind: contextKind,
  };

  const BUSY_MESSAGES: Record<1 | 2 | 3 | 4, { msg: string; sub: string }> = {
    1: { msg: t("wizard.busy1.msg"), sub: t("wizard.busy1.sub") },
    2: { msg: t("wizard.busy2.msg"), sub: t("wizard.busy2.sub") },
    3: { msg: t("wizard.busy3.msg"), sub: t("wizard.busy3.sub") },
    4: { msg: t("wizard.busy4.msg"), sub: t("wizard.busy4.sub") },
  };

  // ── Step-Übergänge ───────────────────────────────────────────────
  async function loadIlos() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.suggestILOs({
        rough_idea: roughIdea,
        target_group: targetGroup,
        context_kind: contextKind,
        strategy: strategyHint.trim() || undefined,
        language: contentLang,
      });
      setIlos(res.ilos);
      goStep(2);
    } catch (e) {
      handleSuggestError(e, "wizard.suggestFailed");
    } finally {
      setBusy(false);
    }
  }

  async function loadTlas() {
    if (ilos.length === 0) {
      setError(t("wizard.needIlo"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.suggestTLAs({
        course_meta: courseMeta,
        ilos,
        strategy: strategyHint.trim() || undefined,
        language: contentLang,
      });
      if (res.tlas.length === 0) {
        setError(t("wizard.noTlas"));
        return;
      }
      setTlas(res.tlas);
      goStep(3);
    } catch (e) {
      handleSuggestError(e, "wizard.suggestFailed");
    } finally {
      setBusy(false);
    }
  }

  async function loadAts() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.suggestATs({
        course_meta: courseMeta,
        ilos,
        tlas,
        strategy: strategyHint.trim() || undefined,
        language: contentLang,
      });
      if (res.ats.length === 0) {
        setError(t("wizard.noAts"));
        return;
      }
      setAts(res.ats);
      goStep(4);
    } catch (e) {
      handleSuggestError(e, "wizard.suggestFailed");
    } finally {
      setBusy(false);
    }
  }

  async function finalize() {
    setBusy(true);
    setError(null);
    try {
      // 1) Course anlegen (CourseCreate akzeptiert KEIN metadata — deshalb
      // direkt danach per PATCH hinterherschicken)
      const course = await api.createCourse({
        title,
        audience: targetGroup,
        format: (effectiveFormat || undefined) as CourseFormat | undefined,
        duration_hours: durationHours ? Number(durationHours) : undefined,
      });
      // Kontext, Rohidee (Seminarbeschreibung), Sprache der Kursinhalte und
      // den Strategie-Freitext als Metadaten persistieren — sie stehen sonst
      // nirgends und fehlen im Export.
      await api.updateCourse(course.id, {
        metadata: {
          context_kind: contextKind,
          rough_idea: roughIdea,
          content_lang: contentLang,
          strategy: { global_text: strategyHint.trim() },
        },
      });
      // 2) ILOs anlegen, IDs für Verlinkung merken
      const iloIds: string[] = [];
      for (const ilo of ilos) {
        const created = await api.createILO(course.id, {
          text: ilo.text,
          action_verb: ilo.action_verb,
          bender_depth: ilo.bender_depth,
        });
        iloIds.push(created.id);
      }
      // 3) TLAs — Alignment-Verknüpfung + Phase aus der Konstruktion mitspeichern
      for (const tla of tlas) {
        await api.createTLA(course.id, {
          type: tla.type,
          description: tla.description,
          duration_minutes: tla.duration_minutes ?? undefined,
          modality: (tla.modality as "presence" | "synchronous" | "asynchronous") || undefined,
          phase: (tla.phase as TLAPhase) || undefined,
          linked_ilo_ids: (tla.linked_ilo_indices ?? [])
            .map((i) => iloIds[i])
            .filter((id): id is string => Boolean(id)),
        });
      }
      // 4) ATs — Alignment-Verknüpfung aus der Konstruktion mitspeichern
      for (const at of ats) {
        await api.createAT(course.id, {
          type: at.type,
          description: at.description,
          format: (at.format as "closed" | "semi_open" | "open") || undefined,
          linked_ilo_ids: (at.linked_ilo_indices ?? [])
            .map((i) => iloIds[i])
            .filter((id): id is string => Boolean(id)),
        });
      }
      navigate(`/courses/${course.id}/workspace`);
    } catch (e) {
      setError(e instanceof APIError ? e.detail : t("wizard.createFailed"));
    } finally {
      setBusy(false);
    }
  }

  // ── Step-Leiste: freie Rückwärts-Navigation ───────────────────────
  // Vorwärts-Guard pro Ziel-Step — spiegelt die Pflicht-Validierung der
  // Weiter-Buttons (Step 1: Pflichtfelder, Step 2: mind. 1 Lernziel,
  // Step 3: kein Guard außer busy).
  const canAdvanceTo = (n: WizardStep): boolean => {
    if (n === 2) {
      return (
        !!contextKind &&
        !!title.trim() &&
        !!targetGroup.trim() &&
        roughIdea.trim().length >= 10
      );
    }
    if (n === 3) return ilos.length > 0;
    return true;
  };

  const handleStepSelect = (n: WizardStep) => {
    if (busy || n === step) return;
    if (n <= maxStep) {
      // besuchter Step — frei anspringbar, State bleibt unverändert
      setError(null);
      setStep(n);
    } else if (n === maxStep + 1 && canAdvanceTo(n)) {
      // wie der Weiter-Button des vorherigen Steps (inkl. KI-Generierung)
      if (n === 2) void loadIlos();
      else if (n === 3) void loadTlas();
      else void loadAts();
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  // Chat-Panel für die Steps 2–4 (unter den Karten, über der Navigation).
  // Einmal definiert, von den Step-Komponenten an der vorgesehenen Stelle
  // gerendert; History/Vorschlag bleiben über Step-Wechsel erhalten.
  const chatPanel =
    step !== 1 ? (
      <DidaktikChat
        step={step}
        courseMeta={courseMeta}
        ilos={ilos}
        tlas={tlas}
        ats={ats}
        strategy={strategyHint.trim() || undefined}
        language={contentLang}
        messages={chatMessages}
        setMessages={setChatMessages}
        proposal={chatProposal}
        setProposal={setChatProposal}
        onApply={applyChatProposal}
        disabled={busy}
      />
    ) : null;

  return (
    <>
      <Header />
      {busy && (
        <LoadingOverlay
          message={BUSY_MESSAGES[step].msg}
          subMessage={BUSY_MESSAGES[step].sub}
        />
      )}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <StepIndicator
          labels={[
            t("wizard.steps.1"),
            t("wizard.steps.2"),
            t("wizard.steps.3"),
            t("wizard.steps.4"),
          ]}
          current={step}
          maxStep={maxStep}
          busy={busy}
          canAdvanceTo={(n) => canAdvanceTo(n as WizardStep)}
          onSelect={(n) => handleStepSelect(n as WizardStep)}
        />

        {quota !== null && (
          <div className="mb-8">
            <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
          </div>
        )}

        {error && (
          <div className="mb-8 p-4 rounded-lg bg-ca-card border border-ca-surface-border text-verdict-red text-sm">
            {error}
          </div>
        )}

        {step === 1 && (
          <Step1Input
            contextKind={contextKind} setContextKind={setContextKind}
            title={title} setTitle={setTitle}
            targetGroup={targetGroup} setTargetGroup={setTargetGroup}
            courseFormat={courseFormat} setCourseFormat={setCourseFormat}
            customFormat={customFormat} setCustomFormat={setCustomFormat}
            durationHours={durationHours} setDurationHours={setDurationHours}
            roughIdea={roughIdea} setRoughIdea={setRoughIdea}
            contentLang={contentLang} setContentLang={setContentLang}
            strategyHint={strategyHint} setStrategyHint={setStrategyHint}
            catalog={catalog}
            busy={busy}
            onNext={loadIlos}
            onCancel={() => navigate("/")}
          />
        )}

        {step === 2 && (
          <Step2Ilos
            ilos={ilos} setIlos={setIlos}
            onMove={moveIlo}
            assistContext={assistContext}
            contentLang={contentLang}
            busy={busy}
            chatPanel={chatPanel}
            onBack={() => setStep(1)}
            onNext={loadTlas}
          />
        )}

        {step === 3 && (
          <Step3Tlas
            tlas={tlas} setTlas={setTlas}
            ilos={ilos}
            catalog={catalog}
            assistContext={assistContext}
            contentLang={contentLang}
            busy={busy}
            chatPanel={chatPanel}
            durationMinutes={durationHours ? Number(durationHours) * 60 : null}
            onBack={() => setStep(2)}
            onNext={loadAts}
          />
        )}

        {step === 4 && (
          <Step4Ats
            ats={ats} setAts={setAts}
            ilos={ilos}
            catalog={catalog}
            assistContext={assistContext}
            contentLang={contentLang}
            busy={busy}
            chatPanel={chatPanel}
            onBack={() => setStep(3)}
            onFinalize={finalize}
          />
        )}
      </div>
    </>
  );
}

// ── Step 1: Input ─────────────────────────────────────────────────────
function Step1Input(props: {
  contextKind: WizardContextKind | null;
  setContextKind: (v: WizardContextKind) => void;
  title: string; setTitle: (v: string) => void;
  targetGroup: string; setTargetGroup: (v: string) => void;
  courseFormat: string; setCourseFormat: (v: string) => void;
  customFormat: string; setCustomFormat: (v: string) => void;
  durationHours: string; setDurationHours: (v: string) => void;
  roughIdea: string; setRoughIdea: (v: string) => void;
  contentLang: ContentLang; setContentLang: (v: ContentLang) => void;
  strategyHint: string; setStrategyHint: (v: string) => void;
  catalog: WizardCatalog | null;
  busy: boolean;
  onNext: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const canNext =
    !!props.contextKind &&
    !!props.title.trim() &&
    !!props.targetGroup.trim() &&
    props.roughIdea.trim().length >= 10;
  const canSuggestIdea = props.title.trim().length >= 3;
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [suggestQuota, setSuggestQuota] = useState<unknown>(null);
  // KI-Vorschlag bei gefülltem Feld: erscheint als Karte unter dem Feld,
  // der Originaltext (Stichworte/Alt-Beschreibung) bleibt bis zum
  // expliziten „Übernehmen" erhalten (Reversibilität).
  const [ideaProposal, setIdeaProposal] = useState<string | null>(null);
  // Kuratierte Format-Liste des gewählten Kontexts (inkl. „Anderes Format").
  const contextFormats = props.contextKind
    ? props.catalog?.context_formats?.[props.contextKind] ?? []
    : [];
  const selectedFormatEntry = contextFormats.find((f) => f.id === props.courseFormat);

  async function suggestIdea() {
    setSuggestBusy(true);
    setSuggestError(null);
    try {
      const res = await api.suggestRoughIdea({
        title: props.title,
        target_group: props.targetGroup || undefined,
        course_format: props.courseFormat || null,
        duration_hours: props.durationHours ? Number(props.durationHours) : null,
        existing_text: props.roughIdea.trim() || undefined,
        strategy: props.strategyHint.trim() || undefined,
        language: props.contentLang,
      });
      const suggestion = res.rough_idea.trim();
      if (props.roughIdea.trim()) {
        // Gefülltes Feld: Vorschlag als Karte anbieten, Original erhalten
        setIdeaProposal(suggestion);
      } else {
        props.setRoughIdea(suggestion);
      }
    } catch (e) {
      if (isQuotaError(e)) setSuggestQuota(e.data);
      else setSuggestError(e instanceof APIError ? e.detail : t("wizard.ideaFailed"));
    } finally {
      setSuggestBusy(false);
    }
  }

  return (
    <section>
      <h2 className="text-3xl font-serif font-semibold text-ca-brand mb-2">{t("wizard.step1.title")}</h2>
      <p className="text-ca-ink mb-8">
        {t("wizard.step1.intro")}
      </p>

      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-ca-ink-muted mb-3">
          {t("wizard.step1.contextLabel")}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CONTEXT_KEYS.map((key) => {
            const active = props.contextKind === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => props.setContextKind(key)}
                className={`p-4 rounded-xl border-2 text-left transition ${
                  active
                    ? "border-ca-accent bg-ca-accent/10"
                    : "border-ca-surface-border bg-ca-card hover:border-ca-accent/40"
                }`}
              >
                <div
                  className={`font-black text-sm uppercase tracking-wide mb-1 ${
                    active ? "text-ca-brand" : "text-ca-surface-ink"
                  }`}
                >
                  {t(CONTEXT_LABEL_KEYS[key].label)}
                </div>
                <div className={`text-[11px] leading-snug ${active ? "text-ca-ink-muted" : "text-ca-surface-muted"}`}>
                  {t(CONTEXT_LABEL_KEYS[key].sub)}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-ca-ink-muted mt-3 italic">
          {t("wizard.step1.contextHint")}
        </p>
      </div>

      <div className="space-y-6 bg-ca-card p-8 rounded-xl border border-ca-surface-border">
        <Field label={t("wizard.step1.titleLabel")}>
          <input
            value={props.title}
            onChange={(e) => props.setTitle(e.target.value)}
            placeholder={t("wizard.step1.titlePlaceholder")}
            className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
          />
        </Field>

        <Field label={t("wizard.step1.audienceLabel")}>
          <input
            value={props.targetGroup}
            onChange={(e) => props.setTargetGroup(e.target.value)}
            placeholder={t("wizard.step1.audiencePlaceholder")}
            className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
          />
        </Field>

        <Field label={t("wizard.step1.formatLabel")}>
          {contextFormats.length > 0 ? (
            <>
              <select
                value={props.courseFormat}
                onChange={(e) => props.setCourseFormat(e.target.value)}
                className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
              >
                <option value="">{t("wizard.step1.formatOptional")}</option>
                {contextFormats.map((f) => (
                  <option key={f.id} value={f.id} title={f.description}>
                    {f.name}
                  </option>
                ))}
              </select>
              {props.courseFormat === "anderes" ? (
                <input
                  value={props.customFormat}
                  onChange={(e) => props.setCustomFormat(e.target.value)}
                  placeholder={t("wizard.step1.customFormatPlaceholder")}
                  className="w-full mt-2 bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
                />
              ) : (
                selectedFormatEntry && (
                  <p className="text-xs text-ca-surface-muted leading-relaxed">
                    {selectedFormatEntry.description}
                  </p>
                )
              )}
            </>
          ) : (
            <input
              value={props.courseFormat}
              onChange={(e) => props.setCourseFormat(e.target.value)}
              placeholder={
                props.contextKind
                  ? t("wizard.step1.formatLater")
                  : t("wizard.step1.formatPickContext")
              }
              className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
            />
          )}
        </Field>

        <Field label={t("wizard.step1.durationLabel")}>
          <input
            type="number"
            step="0.5"
            min="0"
            value={props.durationHours}
            onChange={(e) => props.setDurationHours(e.target.value)}
            placeholder={t("wizard.step1.durationPlaceholder")}
            className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
          />
        </Field>

        <Field label={t("wizard.step1.contentLangLabel")}>
          <select
            value={props.contentLang}
            onChange={(e) => props.setContentLang(e.target.value as ContentLang)}
            className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent"
          >
            <option value="de">{t("common.langDe")}</option>
            <option value="en">{t("common.langEn")}</option>
          </select>
          <p className="text-xs text-ca-surface-muted mt-1 italic">
            {t("wizard.step1.contentLangHint")}
          </p>
        </Field>

        <Field label={t("wizard.step1.strategyLabel")}>
          <textarea
            value={props.strategyHint}
            onChange={(e) => props.setStrategyHint(e.target.value)}
            placeholder={t("wizard.step1.strategyPlaceholder")}
            rows={2}
            className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent resize-y"
          />
          <p className="text-xs text-ca-surface-muted mt-1 italic">
            {t("wizard.step1.strategyHint")}
          </p>
        </Field>

        <FieldWithAction
          label={t("wizard.step1.ideaLabel")}
          action={
            <button
              type="button"
              onClick={suggestIdea}
              disabled={!canSuggestIdea || suggestBusy}
              title={canSuggestIdea
                ? (props.roughIdea.trim()
                  ? t("wizard.step1.ideaSuggestTitleExpand")
                  : t("wizard.step1.ideaSuggestTitle"))
                : t("wizard.step1.ideaSuggestDisabled")}
              className="flex items-center gap-1.5 text-xs font-semibold rounded-md px-2 py-0.5 bg-ca-accent text-ca-brand hover:bg-brand-gold-dark disabled:bg-brand-cream-dark disabled:text-ca-ink-muted disabled:cursor-not-allowed transition"
            >
              <span aria-hidden>✨</span>
              <span>{suggestBusy ? t("common.aiThinking") : t("wizard.step1.ideaSuggest")}</span>
              <CreditCost feature="wizard_suggest" className="text-ca-brand/80" />
            </button>
          }
        >
          <textarea
            value={props.roughIdea}
            onChange={(e) => props.setRoughIdea(e.target.value)}
            placeholder={t("wizard.step1.ideaPlaceholder")}
            rows={5}
            className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent resize-y"
          />
          {suggestQuota !== null && (
            <div className="mt-2">
              <QuotaExhaustedCard detail={suggestQuota} onDismiss={() => setSuggestQuota(null)} />
            </div>
          )}
          {suggestError && (
            <p className="mt-2 text-xs text-verdict-red">{suggestError}</p>
          )}
          {ideaProposal && (
            <div className="mt-3 p-4 rounded-lg border-2 border-ca-accent bg-ca-accent/5">
              <p className="text-xs font-bold uppercase tracking-wider text-ca-surface-accent mb-2">
                {t("wizard.proposalTitle")}
              </p>
              <p className="text-sm text-ca-surface-ink whitespace-pre-wrap mb-3">{ideaProposal}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    props.setRoughIdea(ideaProposal);
                    setIdeaProposal(null);
                  }}
                  className="px-4 py-2 bg-ca-accent text-ca-brand text-sm font-bold rounded-md hover:bg-brand-gold-dark transition"
                >
                  {t("wizard.proposalApply")}
                </button>
                <button
                  type="button"
                  onClick={() => setIdeaProposal(null)}
                  className="px-4 py-2 border border-ca-surface-border text-ca-surface-muted text-sm font-semibold rounded-md hover:text-ca-surface-ink transition"
                >
                  {t("wizard.proposalDiscard")}
                </button>
              </div>
              <p className="text-[11px] text-ca-surface-muted mt-2 italic">{t("wizard.proposalHint")}</p>
            </div>
          )}
        </FieldWithAction>
      </div>

      <div className="flex gap-3 mt-8">
        <button
          onClick={props.onCancel}
          className="px-6 py-3 border border-ca-border text-ca-ink-muted rounded-md font-semibold hover:text-ca-ink transition"
        >
          {t("common.cancel")}
        </button>
        <div className="flex-1" />
        <CreditCost feature="wizard_suggest" className="text-xs text-ca-ink-muted self-center" />
        <button
          onClick={props.onNext}
          disabled={!canNext || props.busy}
          className="px-8 py-3 bg-ca-accent text-ca-brand font-bold rounded-md hover:bg-brand-gold-dark transition disabled:bg-brand-cream-dark disabled:text-ca-ink-muted disabled:cursor-not-allowed"
        >
          {props.busy ? t("wizard.busy1.msg") + "…" : t("wizard.step1.next")}
        </button>
      </div>
    </section>
  );
}

// ── Step 2: ILOs ──────────────────────────────────────────────────────
function Step2Ilos(props: {
  ilos: SuggestedILO[]; setIlos: (v: SuggestedILO[]) => void;
  onMove: (i: number, dir: -1 | 1) => void;
  assistContext: RewriteContext;
  contentLang: ContentLang;
  busy: boolean;
  chatPanel: React.ReactNode;
  onBack: () => void; onNext: () => void;
}) {
  const { t } = useTranslation();
  function update(i: number, patch: Partial<SuggestedILO>) {
    props.setIlos(props.ilos.map((ilo, idx) => (idx === i ? { ...ilo, ...patch } : ilo)));
  }
  function remove(i: number) {
    props.setIlos(props.ilos.filter((_, idx) => idx !== i));
  }
  function add() {
    props.setIlos([...props.ilos, { text: "", action_verb: "", bender_depth: "Verstehen", rationale: "" }]);
  }

  return (
    <section>
      <h2 className="text-3xl font-serif font-semibold text-ca-brand mb-2">{t("wizard.step2.title")}</h2>
      <p className="text-ca-ink-muted mb-8">
        {t("wizard.step2.intro")}
      </p>

      <div className="space-y-4">
        {props.ilos.map((ilo, i) => (
          <div key={i} className="bg-ca-card p-6 rounded-xl border border-ca-surface-border relative">
            <div className="absolute top-4 right-4 flex items-center gap-1">
              {/* Reihenfolge per Hoch/Runter (bewusst kein Drag&Drop) —
                  der Tausch remappt im Wizard auch die index-basierten
                  TLA/AT-Links (moveIlo in Wizard). */}
              <button
                type="button"
                onClick={() => props.onMove(i, -1)}
                disabled={i === 0}
                aria-label={t("wizard.step2.moveUp", { n: i + 1 })}
                title={t("wizard.step2.moveUp", { n: i + 1 })}
                className="px-1.5 text-ca-surface-muted hover:text-ca-surface-accent transition disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => props.onMove(i, 1)}
                disabled={i === props.ilos.length - 1}
                aria-label={t("wizard.step2.moveDown", { n: i + 1 })}
                title={t("wizard.step2.moveDown", { n: i + 1 })}
                className="px-1.5 text-ca-surface-muted hover:text-ca-surface-accent transition disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ↓
              </button>
              <button
                onClick={() => remove(i)}
                className="px-1.5 text-ca-surface-muted hover:text-verdict-red transition"
                title={t("common.remove")}
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="flex items-center gap-1">
                <input
                  value={ilo.action_verb}
                  onChange={(e) => update(i, { action_verb: e.target.value })}
                  placeholder={t("wizard.step2.verbPlaceholder")}
                  className="flex-1 min-w-0 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-sm"
                />
                <AiAssistButton
                  fieldKind="ilo_verb"
                  getText={() => ilo.action_verb}
                  onApply={(v) => update(i, { action_verb: v })}
                  context={props.assistContext}
                  language={props.contentLang}
                  strategyHint={props.assistContext.strategy}
                />
              </div>
              <select
                value={ilo.bender_depth}
                onChange={(e) => update(i, { bender_depth: e.target.value as DepthLabel })}
                className="bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-sm col-span-2"
              >
                {DEPTH_LABELS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="flex items-start gap-1 mb-2">
              <textarea
                value={ilo.text}
                onChange={(e) => update(i, { text: e.target.value })}
                placeholder={t("wizard.step2.textPlaceholder")}
                rows={2}
                className="flex-1 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-sm resize-y"
              />
              <AiAssistButton
                fieldKind="ilo_text"
                getText={() => ilo.text}
                onApply={(v) => update(i, { text: v })}
                context={props.assistContext}
                language={props.contentLang}
                strategyHint={props.assistContext.strategy}
              />
            </div>
            {ilo.rationale && (
              <p className="text-xs text-ca-surface-muted italic font-serif">→ {ilo.rationale}</p>
            )}
          </div>
        ))}
        <button
          onClick={add}
          className="w-full py-3 border border-dashed border-ca-border rounded-md text-ca-ink-muted hover:text-ca-accent-text hover:border-ca-accent transition text-sm font-semibold"
        >
          {t("wizard.step2.add")}
        </button>
      </div>

      {props.chatPanel}

      <div className="flex gap-3 mt-8">
        <button onClick={props.onBack} className="px-6 py-3 border border-ca-border text-ca-ink-muted rounded-md font-semibold hover:text-ca-ink transition">{t("common.back")}</button>
        <div className="flex-1" />
        <CreditCost feature="wizard_suggest" className="text-xs text-ca-ink-muted self-center" />
        <button
          onClick={props.onNext}
          disabled={props.ilos.length === 0 || props.busy}
          className="px-8 py-3 bg-ca-accent text-ca-brand font-bold rounded-md hover:bg-brand-gold-dark transition disabled:bg-brand-cream-dark disabled:text-ca-ink-muted disabled:cursor-not-allowed"
        >
          {props.busy ? t("wizard.busy2.msg") + "…" : t("wizard.step2.next")}
        </button>
      </div>
    </section>
  );
}

// ── Step 3: TLAs ──────────────────────────────────────────────────────
function Step3Tlas(props: {
  tlas: SuggestedTLA[]; setTlas: (v: SuggestedTLA[]) => void;
  ilos: SuggestedILO[];
  catalog: WizardCatalog | null;
  assistContext: RewriteContext;
  contentLang: ContentLang;
  busy: boolean;
  chatPanel: React.ReactNode;
  durationMinutes: number | null;
  onBack: () => void; onNext: () => void;
}) {
  const { t } = useTranslation();
  const phaseOptions = usePhaseOptions();
  function update(i: number, patch: Partial<SuggestedTLA>) {
    props.setTlas(props.tlas.map((tla, idx) => (idx === i ? { ...tla, ...patch } : tla)));
  }
  function remove(i: number) {
    props.setTlas(props.tlas.filter((_, idx) => idx !== i));
  }
  // Wenn LLM einen unbekannten Typ liefert, hängen wir ihn als Custom-Option an,
  // damit der Wert nicht verloren geht.
  const tlaTypes = props.catalog?.tla_types ?? [];
  // Zeit-Plausibilität schon beim Konstruieren: Σ Methoden (Default 45 min) vs. Kursbudget
  const tlaMinutes = props.tlas.reduce((s, x) => s + (x.duration_minutes ?? 45), 0);
  const missingDuration = props.tlas.filter((x) => !x.duration_minutes).length;
  const overBudget = props.durationMinutes != null && tlaMinutes > props.durationMinutes;

  return (
    <section>
      <h2 className="text-3xl font-serif font-semibold text-ca-brand mb-2">{t("wizard.step3.title")}</h2>
      <p className="text-ca-ink-muted mb-4">
        {t("wizard.step3.intro")}
      </p>
      {props.tlas.length > 0 && (
        <p className={`text-xs font-semibold mb-6 ${overBudget ? "text-verdict-red" : "text-ca-ink-muted"}`}>
          Σ {tlaMinutes} min
          {missingDuration > 0 && ` ${t("wizard.step3.missingDuration", { count: missingDuration })}`}
          {props.durationMinutes != null && ` ${t("wizard.step3.ofBudget", { mins: props.durationMinutes })}`}
          {overBudget && ` — ${t("wizard.step3.overBudget")}`}
        </p>
      )}

      <div className="space-y-4">
        {props.tlas.map((tla, i) => (
          <div key={i} className="bg-ca-card p-6 rounded-xl border border-ca-surface-border relative">
            <button onClick={() => remove(i)} title={t("common.remove")} className="absolute top-4 right-4 text-ca-surface-muted hover:text-verdict-red transition">✕</button>
            <TypeSelect
              value={tla.type}
              options={tlaTypes}
              onChange={(v) => update(i, { type: v })}
              placeholder={t("wizard.step3.typePlaceholder")}
            />
            <div className="flex items-start gap-1 my-2">
              <textarea
                value={tla.description}
                onChange={(e) => update(i, { description: e.target.value })}
                placeholder={t("wizard.step3.descPlaceholder")}
                rows={2}
                className="flex-1 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-sm resize-y"
              />
              <AiAssistButton
                fieldKind="tla_description"
                getText={() => tla.description}
                onApply={(v) => update(i, { description: v })}
                context={props.assistContext}
                language={props.contentLang}
                strategyHint={props.assistContext.strategy}
              />
            </div>
            <div className="mb-2">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-ca-surface-muted mb-1">
                {t("wizard.step3.phaseLabel")}
              </label>
              <select
                value={tla.phase ?? "in"}
                onChange={(e) => update(i, { phase: e.target.value })}
                className="bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-xs"
              >
                {phaseOptions.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 text-xs text-ca-surface-muted">
              {tla.duration_minutes != null && <span>~{tla.duration_minutes} min</span>}
              {tla.modality && <span>· {tla.modality}</span>}
            </div>
            <LinkedILOsEditor
              label={t("wizard.linkedIlos")}
              ilos={props.ilos.map((ilo, idx) => ({
                key: idx,
                label: `LZ ${idx + 1}`,
                title: ilo.text || ilo.action_verb,
              }))}
              selected={tla.linked_ilo_indices}
              onToggle={(idx) =>
                update(i, {
                  linked_ilo_indices: tla.linked_ilo_indices.includes(idx)
                    ? tla.linked_ilo_indices.filter((x) => x !== idx)
                    : [...tla.linked_ilo_indices, idx],
                })
              }
            />
            {tla.rationale && (
              <p className="text-xs text-ca-surface-muted italic font-serif mt-2">→ {tla.rationale}</p>
            )}
          </div>
        ))}
      </div>

      {props.chatPanel}

      <div className="flex gap-3 mt-8">
        <button onClick={props.onBack} className="px-6 py-3 border border-ca-border text-ca-ink-muted rounded-md font-semibold hover:text-ca-ink transition">{t("common.back")}</button>
        <div className="flex-1" />
        <CreditCost feature="wizard_suggest" className="text-xs text-ca-ink-muted self-center" />
        <button
          onClick={props.onNext}
          disabled={props.busy}
          className="px-8 py-3 bg-ca-accent text-ca-brand font-bold rounded-md hover:bg-brand-gold-dark transition disabled:bg-brand-cream-dark disabled:text-ca-ink-muted disabled:cursor-not-allowed"
        >
          {props.busy ? t("wizard.busy3.msg") + "…" : t("wizard.step3.next")}
        </button>
      </div>
    </section>
  );
}

// ── Step 4: ATs ───────────────────────────────────────────────────────
function Step4Ats(props: {
  ats: SuggestedAT[]; setAts: (v: SuggestedAT[]) => void;
  ilos: SuggestedILO[];
  catalog: WizardCatalog | null;
  assistContext: RewriteContext;
  contentLang: ContentLang;
  busy: boolean;
  chatPanel: React.ReactNode;
  onBack: () => void; onFinalize: () => void;
}) {
  const { t } = useTranslation();
  function update(i: number, patch: Partial<SuggestedAT>) {
    props.setAts(props.ats.map((at, idx) => (idx === i ? { ...at, ...patch } : at)));
  }
  function remove(i: number) {
    props.setAts(props.ats.filter((_, idx) => idx !== i));
  }
  const atTypes = props.catalog?.at_types ?? [];
  const atFormats = props.catalog?.at_formats ?? [];

  return (
    <section>
      <h2 className="text-3xl font-serif font-semibold text-ca-brand mb-2">{t("wizard.step4.title")}</h2>
      <p className="text-ca-ink-muted mb-8">
        {t("wizard.step4.intro")}
      </p>

      <div className="space-y-4">
        {props.ats.map((at, i) => (
          <div key={i} className="bg-ca-card p-6 rounded-xl border border-ca-surface-border relative">
            <button onClick={() => remove(i)} title={t("common.remove")} className="absolute top-4 right-4 text-ca-surface-muted hover:text-verdict-red transition">✕</button>
            <TypeSelect
              value={at.type}
              options={atTypes}
              onChange={(v) => update(i, { type: v })}
              placeholder={t("wizard.step4.typePlaceholder")}
            />
            <div className="flex items-start gap-1 my-2">
              <textarea
                value={at.description}
                onChange={(e) => update(i, { description: e.target.value })}
                placeholder={t("wizard.step4.descPlaceholder")}
                rows={2}
                className="flex-1 bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-sm resize-y"
              />
              <AiAssistButton
                fieldKind="at_description"
                getText={() => at.description}
                onApply={(v) => update(i, { description: v })}
                context={props.assistContext}
                language={props.contentLang}
                strategyHint={props.assistContext.strategy}
              />
            </div>
            {atFormats.length > 0 && (
              <div className="mb-2">
                <select
                  value={at.format ?? ""}
                  onChange={(e) => update(i, { format: e.target.value || null })}
                  className="bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-xs"
                >
                  <option value="">{t("wizard.step4.formatPlaceholder")}</option>
                  {atFormats.map((f) => (
                    <option key={f.id} value={f.id}>{f.name} — {f.description}</option>
                  ))}
                </select>
              </div>
            )}
            <LinkedILOsEditor
              label={t("wizard.linkedIlos")}
              ilos={props.ilos.map((ilo, idx) => ({
                key: idx,
                label: `LZ ${idx + 1}`,
                title: ilo.text || ilo.action_verb,
              }))}
              selected={at.linked_ilo_indices}
              onToggle={(idx) =>
                update(i, {
                  linked_ilo_indices: at.linked_ilo_indices.includes(idx)
                    ? at.linked_ilo_indices.filter((x) => x !== idx)
                    : [...at.linked_ilo_indices, idx],
                })
              }
            />
            {at.rationale && (
              <p className="text-xs text-ca-surface-muted italic font-serif mt-2">→ {at.rationale}</p>
            )}
          </div>
        ))}
      </div>

      {props.chatPanel}

      <div className="flex gap-3 mt-8">
        <button onClick={props.onBack} className="px-6 py-3 border border-ca-border text-ca-ink-muted rounded-md font-semibold hover:text-ca-ink transition">{t("common.back")}</button>
        <div className="flex-1" />
        <button
          onClick={props.onFinalize}
          disabled={props.busy}
          className="px-8 py-3 bg-ca-accent text-ca-brand font-bold rounded-md hover:bg-brand-navy-light transition disabled:bg-brand-cream-dark disabled:text-ca-ink-muted disabled:cursor-not-allowed"
        >
          {props.busy ? t("wizard.busy4.msg") + "…" : t("wizard.step4.finalize")}
        </button>
      </div>
    </section>
  );
}

// ── Field-Helper ──────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-ca-surface-muted mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function FieldWithAction({
  label,
  action,
  children,
}: {
  label: string;
  action: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="block text-xs font-bold uppercase tracking-wider text-ca-surface-muted">
          {label}
        </span>
        {action}
      </div>
      {children}
    </div>
  );
}

// ── TypeSelect: Dropdown mit Klartext-Labels ───────────────────────────
// Wenn der KI-Vorschlag eine ID liefert, die nicht im Katalog ist (z.B. weil
// das LLM eine eigene Methodenbezeichnung erfunden hat), behalten wir den
// Wert als Custom-Option, damit nichts verloren geht. Der Katalog ist optional
// (kann beim Mount fehlschlagen) — dann fallback auf Free-Text-Edit.
function TypeSelect(props: {
  value: string;
  options: { id: string; name: string; description?: string | null }[];
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const { t } = useTranslation();
  if (props.options.length === 0) {
    // Catalog noch nicht da → Free-Text-Edit
    return (
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="w-full bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-sm font-semibold"
      />
    );
  }
  const knownIds = new Set(props.options.map((o) => o.id));
  const isCustom = props.value && !knownIds.has(props.value);
  return (
    <select
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      className="w-full bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink placeholder:text-ca-surface-muted outline-none focus:border-ca-accent text-sm font-semibold"
    >
      <option value="">{props.placeholder}</option>
      {props.options.map((o) => (
        <option key={o.id} value={o.id} title={o.description ?? undefined}>
          {o.name}
        </option>
      ))}
      {isCustom && (
        <option value={props.value}>
          {props.value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} {t("common.customLabel")}
        </option>
      )}
    </select>
  );
}
